import type { ConsoleColorCode } from '@ricoh/ria.client.core/constants/core/console.type';

import { CONSOLE_COLOR_CODE } from '@ricoh/ria.client.core/constants/core/console';

import type { LogLevel } from './_api.type';

/**
 * Color text format.
 *
 * @param {string} text Text.
 * @param {ConsoleColorCode['value']} color Color code.
 * @param {ConsoleColorCode['value']} backgroundColor Background color code.
 *
 * @returns {string} Color text.
 */
export function colorText(
  text: string,
  color: ConsoleColorCode['value'] = CONSOLE_COLOR_CODE.DEFAULT,
  backgroundColor: ConsoleColorCode['value'] = CONSOLE_COLOR_CODE.BG_DEFAULT,
): string {
  return `\x1b[${color};${backgroundColor}m${text}\x1b[0m`;
}

/**
 * Request text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Request text message.
 */
export function requestText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.MAGENTA);
}

/**
 * Response text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Response text message.
 */
export function responseText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.GREEN);
}

/**
 * Error text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Error text message.
 */
export function errorText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.RED);
}

/**
 * Warn text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Warn text message.
 */
export function warnText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.YELLOW);
}

/**
 * Info text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Info text message.
 */
export function infoText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.CYAN);
}

/**
 * Log text message.
 *
 * @param {string} text Text.
 *
 * @returns {string} Log text message.
 */
export function logText(text: string): string {
  return colorText(text, CONSOLE_COLOR_CODE.GRAY);
}

/**
 * Create LogLevel text format.
 *
 * @param {LogLevel} level Log level.
 *
 * @returns {string} LogLevel text format.
 */
export function logLevelText(level: LogLevel): string {
  let backgroundColor: ConsoleColorCode['value'];
  switch (level) {
    case 'info':
      backgroundColor = CONSOLE_COLOR_CODE.BG_CYAN;
      break;

    case 'warn':
      backgroundColor = CONSOLE_COLOR_CODE.BG_YELLOW;
      break;

    case 'error':
      backgroundColor = CONSOLE_COLOR_CODE.BG_RED;
      break;

    case 'log':
    default:
      backgroundColor = CONSOLE_COLOR_CODE.BG_DARK_GRAY;
      break;
  }

  return colorText(` ${level.toUpperCase()} `, CONSOLE_COLOR_CODE.WHITE, backgroundColor);
}

/**
 * Key text message.
 *
 * @param {string} key Key string.
 *
 * @returns {string} Request text message.
 */
export function keyText(key: string): string {
  return colorText(key?.toString() ?? '-', CONSOLE_COLOR_CODE.CYAN);
}
